/* Colour themes.
 *
 * §8 fixes one palette. This keeps that palette as the default and lets it be
 * swapped, because the person reading this book every day should not have to
 * live with someone else's taste.
 *
 * A theme sets the surfaces, the text, the four semantic colours and the accent.
 * It does not touch the position palette: those twelve colours are chosen to be
 * told apart from one another, and a theme is not a reason to make two holdings
 * look alike. Nor does it touch the rule that colour is never the only signal —
 * every up and down still ships with an arrow, every breach with a word. */

export interface Theme {
  id: string;
  name: string;
  blurb: string;
  /** Drives the browser's own chrome: form controls, scrollbars, date pickers.
      Without it a light theme gets dark native widgets. */
  scheme: 'dark' | 'light';
  vars: Record<string, string>;
  /** Four swatches for the picker: accent, up, down, a surface. */
  swatches: [string, string, string, string];
}

/** The background's two radial glows, as a ready-made value. */
function glow(a: string, b: string): string {
  return `radial-gradient(58vw 44vh at 92% -6%, ${a}, transparent 68%), ` +
         `radial-gradient(52vw 40vh at 4% -4%, ${b}, transparent 66%)`;
}

export const THEMES: Theme[] = [
  {
    id: 'violet',
    name: 'Violet',
    blurb: 'The original: deep navy glass, violet accent.',
    scheme: 'dark',
    swatches: ['#7C6BFF', '#2BE59B', '#FF5470', '#0B0D18'],
    vars: {
      '--bg': '#06070E',
      '--card': 'rgba(255,255,255,.035)',
      '--edge': 'rgba(255,255,255,.075)',
      '--edge2': 'rgba(255,255,255,.13)',
      '--text': '#F2F5FC',
      '--dim': '#7A88A6',
      '--dim2': '#A8B4CC',
      '--up': '#2BE59B',
      '--down': '#FF5470',
      '--warn': '#FFB020',
      '--crit': '#FF3D6B',
      '--accent': '#7C6BFF',
      '--capital': '#4A5878',
      '--glow': glow('rgba(124,107,255,.22)', 'rgba(0,217,255,.14)'),
      '--float': 'rgba(14,16,26,.86)',
      '--float-solid': '#0C0E19',
    },
  },
  {
    id: 'neon',
    name: 'Neon',
    blurb: 'Black glass and neon green. Highest contrast of the set.',
    scheme: 'dark',
    swatches: ['#9AE600', '#22C55E', '#F43F5E', '#000000'],
    vars: {
      '--bg': '#000000',
      '--card': 'rgba(255,255,255,.045)',
      '--edge': 'rgba(255,255,255,.09)',
      '--edge2': 'rgba(154,230,0,.22)',
      '--text': '#F4FBE8',
      '--dim': '#79856B',
      '--dim2': '#B4C3A4',
      '--up': '#9AE600',
      '--down': '#F43F5E',
      '--warn': '#FACC15',
      '--crit': '#FF2D55',
      '--accent': '#9AE600',
      '--capital': '#4B5563',
      '--glow': glow('rgba(154,230,0,.16)', 'rgba(34,197,94,.10)'),
      '--float': 'rgba(8,10,6,.88)',
      '--float-solid': '#080A06',
    },
  },
  {
    id: 'midnight',
    name: 'Midnight',
    blurb: 'Cool blues on near-black. Quiet at night.',
    scheme: 'dark',
    swatches: ['#60A5FA', '#34D399', '#FB7185', '#080B14'],
    vars: {
      '--bg': '#05070D',
      '--card': 'rgba(255,255,255,.04)',
      '--edge': 'rgba(148,180,255,.10)',
      '--edge2': 'rgba(148,180,255,.18)',
      '--text': '#EAF1FF',
      '--dim': '#6E7F9E',
      '--dim2': '#A2B2CE',
      '--up': '#34D399',
      '--down': '#FB7185',
      '--warn': '#FBBF24',
      '--crit': '#F43F5E',
      '--accent': '#60A5FA',
      '--capital': '#3E5074',
      '--glow': glow('rgba(96,165,250,.20)', 'rgba(56,189,248,.12)'),
      '--float': 'rgba(10,14,24,.86)',
      '--float-solid': '#0A0E18',
    },
  },
  {
    id: 'graphite',
    name: 'Graphite',
    blurb: 'Monochrome. Nothing competes with the numbers.',
    scheme: 'dark',
    swatches: ['#E5E7EB', '#4ADE80', '#F87171', '#0C0C0D'],
    vars: {
      '--bg': '#0A0A0B',
      '--card': 'rgba(255,255,255,.045)',
      '--edge': 'rgba(255,255,255,.09)',
      '--edge2': 'rgba(255,255,255,.16)',
      '--text': '#F4F4F5',
      '--dim': '#7C7C85',
      '--dim2': '#B0B0B8',
      '--up': '#4ADE80',
      '--down': '#F87171',
      '--warn': '#FBBF24',
      '--crit': '#EF4444',
      '--accent': '#E5E7EB',
      '--capital': '#52525B',
      '--glow': glow('rgba(255,255,255,.07)', 'rgba(255,255,255,.04)'),
      '--float': 'rgba(20,20,22,.88)',
      '--float-solid': '#141416',
    },
  },
  {
    id: 'ocean',
    name: 'Ocean',
    blurb: 'Teal and deep blue-green.',
    scheme: 'dark',
    swatches: ['#2DD4BF', '#34D399', '#FB7185', '#04100F'],
    vars: {
      '--bg': '#03100F',
      '--card': 'rgba(255,255,255,.04)',
      '--edge': 'rgba(45,212,191,.12)',
      '--edge2': 'rgba(45,212,191,.22)',
      '--text': '#E8FAF7',
      '--dim': '#6A8A87',
      '--dim2': '#9FBFBB',
      '--up': '#34D399',
      '--down': '#FB7185',
      '--warn': '#FBBF24',
      '--crit': '#F43F5E',
      '--accent': '#2DD4BF',
      '--capital': '#3D6360',
      '--glow': glow('rgba(45,212,191,.18)', 'rgba(56,189,248,.12)'),
      '--float': 'rgba(6,26,24,.88)',
      '--float-solid': '#061A18',
    },
  },
  {
    id: 'forest',
    name: 'Forest',
    blurb: 'Natural greens, warm warnings.',
    scheme: 'dark',
    swatches: ['#4ADE80', '#22C55E', '#EF4444', '#050D07'],
    vars: {
      '--bg': '#040C07',
      '--card': 'rgba(255,255,255,.04)',
      '--edge': 'rgba(74,222,128,.11)',
      '--edge2': 'rgba(74,222,128,.20)',
      '--text': '#ECFAF0',
      '--dim': '#6E8874',
      '--dim2': '#A5BFAC',
      '--up': '#22C55E',
      '--down': '#EF4444',
      '--warn': '#F59E0B',
      '--crit': '#DC2626',
      '--accent': '#4ADE80',
      '--capital': '#3E5C48',
      '--glow': glow('rgba(74,222,128,.16)', 'rgba(250,204,21,.08)'),
      '--float': 'rgba(8,20,12,.88)',
      '--float-solid': '#08140C',
    },
  },
  {
    id: 'ember',
    name: 'Ember',
    blurb: 'Warm sand and burnt orange.',
    scheme: 'dark',
    swatches: ['#FB923C', '#84CC16', '#F43F5E', '#100906'],
    vars: {
      '--bg': '#0D0805',
      '--card': 'rgba(255,255,255,.045)',
      '--edge': 'rgba(251,146,60,.12)',
      '--edge2': 'rgba(251,146,60,.22)',
      '--text': '#FBF1E8',
      '--dim': '#8A7565',
      '--dim2': '#C4AF9E',
      '--up': '#84CC16',
      '--down': '#F43F5E',
      '--warn': '#FBBF24',
      '--crit': '#EF4444',
      '--accent': '#FB923C',
      '--capital': '#6B5443',
      '--glow': glow('rgba(251,146,60,.18)', 'rgba(234,88,12,.10)'),
      '--float': 'rgba(22,14,9,.88)',
      '--float-solid': '#160E09',
    },
  },
  {
    id: 'rose',
    name: 'Rose',
    blurb: 'Muted plum with a rose accent.',
    scheme: 'dark',
    swatches: ['#F472B6', '#34D399', '#FB7185', '#0E0710'],
    vars: {
      '--bg': '#0B060D',
      '--card': 'rgba(255,255,255,.045)',
      '--edge': 'rgba(244,114,182,.12)',
      '--edge2': 'rgba(244,114,182,.22)',
      '--text': '#FAEDF6',
      '--dim': '#8A7186',
      '--dim2': '#C2A9BD',
      '--up': '#34D399',
      '--down': '#FB7185',
      '--warn': '#FBBF24',
      '--crit': '#F43F5E',
      '--accent': '#F472B6',
      '--capital': '#654860',
      '--glow': glow('rgba(244,114,182,.16)', 'rgba(168,85,247,.12)'),
      '--float': 'rgba(20,10,22,.88)',
      '--float-solid': '#140A16',
    },
  },
  {
    id: 'paper',
    name: 'Paper',
    blurb: 'Light. For a bright room or a desk by a window.',
    scheme: 'light',
    swatches: ['#4F46E5', '#059669', '#DC2626', '#F7F7FA'],
    vars: {
      '--bg': '#F6F7FB',
      '--card': 'rgba(255,255,255,.86)',
      '--edge': 'rgba(15,23,42,.10)',
      '--edge2': 'rgba(15,23,42,.18)',
      '--text': '#0F172A',
      '--dim': '#64748B',
      '--dim2': '#475569',
      '--up': '#059669',
      '--down': '#DC2626',
      '--warn': '#B45309',
      '--crit': '#BE123C',
      '--accent': '#4F46E5',
      '--capital': '#94A3B8',
      '--glow': glow('rgba(79,70,229,.10)', 'rgba(14,165,233,.08)'),
      '--float': 'rgba(255,255,255,.92)',
      '--float-solid': '#FFFFFF',
      /* On a light surface the deep shadow of the dark themes reads as dirt. */
      '--shadow': '0 10px 30px -14px rgba(15,23,42,.22), 0 1px 3px -1px rgba(15,23,42,.10)',
      '--shadow-lift': '0 20px 44px -18px rgba(15,23,42,.28)',
      /* And the edge treatment inverts: a white highlight on a white card is
         nothing, and the deep bottom line of the dark themes is a smudge. */
      '--edge-light': 'rgba(255,255,255,.9)',
      '--edge-dark': 'rgba(15,23,42,.06)',
    },
  },
];

export const DEFAULT_THEME = 'violet';

export function themeById(id: string | undefined): Theme {
  return THEMES.find((t) => t.id === id) ?? THEMES[0]!;
}

function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.slice(0, 6);
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Relative luminance, per WCAG. */
function luminance(hex: string): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi! + 0.05) / (lo! + 0.05);
}

/** What to print on a filled colour: whichever of black or white actually reads
    better against it. Guessing from a luminance threshold got white onto an
    orange button at a contrast of 2.3 — measuring both cannot. */
function readableOn(fill: string): string {
  const dark = '#0A0C10';
  const light = '#FFFFFF';
  return contrast(dark, fill) >= contrast(light, fill) ? dark : light;
}

/** Semantic colours are used at low opacity for tinted surfaces too, so those
    are derived here rather than hand-written into every theme. */
function derived(vars: Record<string, string>): Record<string, string> {
  const tint = (hex: string, alpha: number) => {
    const [r, g, b] = rgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  const crit = vars['--crit'] ?? '#FF3D6B';
  const warn = vars['--warn'] ?? '#FFB020';
  const up = vars['--up'] ?? '#2BE59B';
  const accent = vars['--accent'] ?? '#7C6BFF';
  const onAccent = readableOn(accent);
  const onCrit = readableOn(crit);

  return {
    '--on-accent': onAccent,
    '--on-crit': onCrit,
    '--crit-bg': tint(crit, 0.09),
    '--crit-edge': tint(crit, 0.3),
    '--warn-bg': tint(warn, 0.08),
    '--warn-edge': tint(warn, 0.26),
    '--up-bg': tint(up, 0.1),
    '--accent-bg': tint(accent, 0.12),
    '--accent-edge': tint(accent, 0.34),
  };
}

/** Writes a theme onto the document. Called on load and whenever it changes. */
/* How far the background wash comes up, as a multiplier on the theme's own.
 *
 * Clamped rather than trusted: this reaches a CSS variable, and a stored value
 * from a future version — or a hand-edited one — should dim or brighten the
 * page, never blank it or paint over the text. Absent means 1, which is the
 * theme exactly as it was designed. */
export const GLOW_RANGE = { min: 0, max: 2, step: 0.05 } as const;

export function applyGlowStrength(value: number | undefined): number {
  const wanted = typeof value === 'number' && Number.isFinite(value) ? value : 1;
  const strength = Math.min(GLOW_RANGE.max, Math.max(GLOW_RANGE.min, wanted));
  document.documentElement.style.setProperty('--glow-strength', String(strength));
  return strength;
}

export function applyTheme(id: string | undefined): Theme {
  const theme = themeById(id);
  const root = document.documentElement;
  const all = { ...theme.vars, ...derived(theme.vars) };
  for (const [name, value] of Object.entries(all)) root.style.setProperty(name, value);
  root.dataset['theme'] = theme.id;
  root.style.colorScheme = theme.scheme;
  // The installed app paints its window chrome from this.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.vars['--bg'] ?? '#06070E');
  return theme;
}
